import React from 'react';

import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import { formatExact, formatValue, memberLabel } from './format';
import type { Envelope, LensDocumentData } from './types';

type BlockDataProps = { block: { id: string; title: string }; data?: LensDocumentData; onDownload?: () => void; onShowSql?: () => void };

const SHOWN_ROWS = 200;

function Rows({ envelope, onDownload, onShowSql }: { envelope: Envelope; onDownload?: () => void; onShowSql?: () => void }) {
  const rows = envelope.rows.slice(0, SHOWN_ROWS);
  return (
    <Stack spacing={0.5}>
      <Box sx={{ maxHeight: 'var(--rows-h, 520px)', mx: 'calc(-1 * var(--pad, 0px))', overflow: 'auto' }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', '& th, & td': { borderBottom: 1, borderColor: 'divider', px: 1, py: 0.5, whiteSpace: 'nowrap' }, '& th': { bgcolor: 'background.paper', position: 'sticky', top: 0, textAlign: 'left' }, '& td.n': { textAlign: 'right' } }}>
          <thead>
            <tr>
              {envelope.columns.map((column) => (
                <th key={column.name} title={column.definition}>
                  {column.role === 'time' ? 'Date' : column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {envelope.columns.map((column) => {
                  const value = row[column.name];
                  return column.role === 'measure' ? (
                    <td className="n" key={column.name} title={formatExact(value as number | null, column.format)}>
                      {formatValue(value as number | null, column.format, { numbers: 'full', decimals: column.decimals })}
                    </td>
                  ) : (
                    <td key={column.name}>{value === null || value === undefined ? '' : memberLabel(String(value))}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography color="text.secondary" sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }} variant="caption">
        <Box component="span" sx={{ width: '100%' }}>{envelope.rows.length > SHOWN_ROWS ? `First ${SHOWN_ROWS} of ${envelope.rows.length.toLocaleString('en-AU')} rows; CSV has them all.` : `${envelope.rows.length.toLocaleString('en-AU')} rows for ${envelope.period.current.label}.`}</Box>
        {onShowSql ? (
          <Button onClick={onShowSql} size="small" startIcon={<CodeOutlinedIcon sx={{ fontSize: '16px !important' }} />} sx={{ fontSize: 12.5, minWidth: 0, ml: 0, whiteSpace: 'nowrap', px: 0.75, textTransform: 'none' }} variant="text">
            Show SQL
          </Button>
        ) : null}
        {onDownload ? (
          <Button onClick={onDownload} size="small" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '16px !important' }} />} sx={{ fontSize: 12.5, minWidth: 0, ml: 0, whiteSpace: 'nowrap', px: 0.75, textTransform: 'none' }} variant="text">
            Download CSV
          </Button>
        ) : null}
      </Typography>
    </Stack>
  );
}

export default function BlockData({ block, data, onDownload, onShowSql }: BlockDataProps) {
  const planned = data?.blocks[block.id];
  const envelopes = (planned?.queries ?? (planned?.query ? [planned.query] : [])).map((id) => data?.queries[id]).filter((entry): entry is Envelope => Boolean(entry));
  return (
    <Stack aria-label={`Data behind ${block.title}`} data-lens-data-view={block.id} role="region" spacing={2}>
      {envelopes.map((envelope) => (envelope.status === 'error' ? (
        <Typography color="error" key={envelope.query} variant="body2">
          {envelope.error?.message}
        </Typography>
      ) : (
        <Rows envelope={envelope} key={envelope.query} onDownload={onDownload} onShowSql={onShowSql} />
      )))}
    </Stack>
  );
}
